import {
	Box,
	CircularProgress,
	Divider,
	FormControlLabel,
	IconButton,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { CrossIcon, DeleteIcon, DuplicateIcon, EditIcon, LockIcon, TickIcon } from '../Icons';
import SearchBar from '../SearchBar';
import CustomButton from '../CustomButton/CustomButton';
import CustomDataGrid2 from '../CustomDataGrid/CustomDataGrid2';
import CustomSwitch from '../CustomSwitch/CustomSwitch';
import CustomModal from '../CustomModal/CustomModal';
import ProductAdd from '../Products/ProductAdd';
import { GridFilterAltIcon } from '@mui/x-data-grid';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import {
	Catalog,
	CustomAlertType,
	SelectedItems,
	SelectedProduct,
	priceRegex,
} from '@src/pages/Catalogs';
import { formatPriceValue, getUniqueObjectsByKey, truncateField } from '@src/helpers/Helpers';
import DataNotFound from '../DataItemFound/DataNotFound';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { useParams } from 'react-router-dom';
import { generateCancelToken } from '@src/helpers/ApiHelper';
import { AxiosError, AxiosResponse } from 'axios';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import {
	setAllProducts,
	setPageNumber,
	setSupplierCatalog,
} from '@src/store/reducers/catalogReducer';
import { Option } from '../CustomSelect/LabeledSelectField';
import CatalogContainer from './CatalogContainer';
import moment from 'moment';
import ProductDetails from '../ProductDetails/ProductDetails';
import { IsMobileWidth } from '../MediaQueryUtils';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import CustomPermission from '../Permissions/CustomPermission';
import CustomDrawer from '../CustomDrawer/CustomDrawer';
import PaginatedProductData from '@src/pages/Catalogs/Components/PaginatedProductData';
import CustomTooltip from '../CustomTootip/CustomTooltip';
import { Filters, Product, TimeoutHandler } from '@src/interfaces/type';
import ProductCard from '../CustomCard/ProductCard';
import CustomFilter from '../CustomFilter/CustomFilter';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: auto;
	padding: 20px;
	gap: 20px;
	overflow: scroll;
	@media screen and (max-width: 750px) {
		padding: 0 15px;
		margin-bottom: 68px;
	}
`;

const GridContainer = styled.div`
	display: grid;
	grid-template-rows: auto auto;
	grid-row-gap: 20px;
	margin-top: 7px;
`;

const GridRow = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	grid-column-gap: 10px;
`;

interface GridItemProps {
	width?: string;
}

const GridItem = styled.div<GridItemProps>`
	display: flex;
	align-items: flex-start;
	font-size: 16px;
	font-weight: 750;
	color: var(--color-font-grey);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	min-width: ${(props) => props.width || '320px'};
	gap: 5px;

	span {
		font-weight: 700;
	}
`;

const EditContainer = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	width: 100%;
	height: 80px;
	@media screen and (max-width: 750px) {
		height: auto;
	}
`;

const StyledDataGridHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	@media screen and (max-width: 750px) {
		gap: 15px;
		flex-direction: column;
	}
`;

const SelectedItemsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 5px;
	font-size: 16px;
	font-weight: 750;
	line-height: 21.82px;
	transition: width 0.3s ease;
`;

const StyledTextFieldCustomerPrice = styled(TextField)`
	input {
		height: 47px;
		width: 151px;
		box-sizing: border-box;
		border: 1.5px solid #acacac;
		background: var(--chat-header-bg);
		text-align: center;
	}
`;

const StyledBtnConatiner = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;
	justify-content: center;
	@media screen and (max-width: 360px) {
		justify-content: right;
		width: 100%;
	}
`;

interface MediaQueries {
	'@media (max-width: 600px)': React.CSSProperties;
}

const stylingDeleteModal: React.CSSProperties & MediaQueries = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '306px',
	height: '375px',
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	'@media (max-width: 600px)': {
		width: '306px',
	},
};

const productDetailsModalStyles = {
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	maxWidth: '600px',
	maxHeight: '444px',
	'@media (max-width: 600px)': {
		width: '90%',
		maxWidth: '90%',
		maxHeight: '645px',
		height: '645px',
	},
};

//For Mobile View
const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	width: 100%;
	height: 68px;
	padding: 20px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 100;
	position: fixed;
	bottom: 0;
`;

const CatalogForm = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	width: 100%;
	padding: 20px;
	gap: 15px;
	@media screen and (max-width: 600px) {
		flex-direction: column;
		justify-content: center;
	}
`;

const CatalogFields = styled.div<GridItemProps>`
	display: flex;
	align-items: flex-start;
	font-size: 16px;
	font-weight: 750;
	color: var(--color-font-grey);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	min-width: ${(props) => props.width || '320px'};
	span {
		font-weight: 700;
	}
`;

const fieldContainerStyling = { display: 'flex', alignItems: 'center', justifyContent: 'center' };

type CatalogsListingProps = {
	customers: any;
	catalogs: Catalog[];
	setCatalogs: (data: Catalog[]) => void;
	filteredCatalogs: Catalog[];
	setFilteredCatalogs: (data: Catalog[]) => void;
	selectedRow: any;
	setSelectedRow: any;
	displayCustomError: (data: AxiosError) => void;
	alert: CustomAlertType;
	setAlert: (data: CustomAlertType) => void;
	setApiCall: (data: boolean) => void;
	filtersMC: Filters;
	setFiltersMC: (data: Filters) => void;
};

export const CatalogsEditable: React.FC<CatalogsListingProps> = ({
	customers,
	catalogs,
	setCatalogs,
	filteredCatalogs,
	setFilteredCatalogs,
	selectedRow,
	setSelectedRow,
	displayCustomError,
	alert,
	setAlert,
	setApiCall,
	filtersMC,
	setFiltersMC,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const { id, catalogId } = useParams();
	const cancelTokenSourceRef: any = useRef();
	const dispatch = useAppDispatch();
	const supplierCatalog: any = useAppSelector((state) => state?.supplierCatalog?.value);
	const allProducts: any = useAppSelector((state) => state?.supplierCatalog?.allProducts);
	const pageNumber: any = useAppSelector((state) => state?.supplierCatalog?.pageNumber);
	const [loadingProducts, setLoadingProducts] = useState(false);

	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);

	const [apiCallProducts, setApiCallProducts] = useState(false);
	//Catalog
	const [draft, setDraft] = useState(false);
	const [catalogName, setCatalogName] = useState<string>('');
	const [customerId, setCustomerId] = useState<string>('');
	const [effectToDate, setEffectToDate] = useState<Date | string | null>(null);
	const [effectFromDate, setEffectFromDate] = useState<Date | string | null>(null);
	const [disable, setDisable] = useState(false);
	const [editCatalog, setEditCatalog] = useState(false);
	const [categoryId, setCategoryId] = useState('');
	const [searchValue, setSearchValue] = useState('');
	const [searching, setSearching] = useState(false);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [products, setProducts] = useState<Product[]>([]);

	//Product

	const [search, setSearch] = useState('');
	const [addProductModal, setAddProductModal] = useState(false);
	const [productDetailsModal, setProductDetailsModal] = useState(false);
	const [deleteProductModal, setdeleteProductModal] = useState(false);
	const [catalogProducts, setCatalogProducts] = useState<Product[]>([]);
	const [catalogProductIds, setCatalogProductIds] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const [btnLoading, setBtnLoading] = useState(false);
	const [selectedItems, setSelectedItems] = useState<SelectedItems[]>([]);
	const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
	const [productCount, setProductCount] = useState(0);
	const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
	const [productDetailsData, setProductDetailsData] = useState([]);
	const [productDetails, setProductDetails] = useState<Product | null>(null);
	const [detailsMiodal, setDetailsModal] = useState(false);
	const [filters, setFilters] = useState<Filters | null>(null);
	const [activeFilter, setActiveFilter] = useState('');

	const { isDrawerOpen } = useAppSelector((state) => state?.UI);
	const isMobileWidth = IsMobileWidth();

	const timeoutHandler = useRef<TimeoutHandler>(null);

	//Permission
	const checkPermission = usePermission();
	const canUpdateCatalogs = checkPermission(
		ALL_PERMISSION_MAP?.SUPPLIER?.CATALOG?.UPDATE,
		catalogId
	);
	const canViewProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.VIEW, id);

	const pageContainerStyles: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		overflow: 'scroll',
		width:
			isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 240px)'
				: !isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 64px)'
				: '100%',
	};
	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	const editSupplierCatalog = () => {
		setBtnLoading(true);
		setDisable(true);

		const data = {
			name: catalogName,
			status: draft ? 'Published' : 'Draft',
			from: effectFromDate,
			to: effectToDate,
			catalogId: catalogId,
		};

		apiService.updateCatalog(
			data,
			(response: AxiosResponse) => {
				const updatedCatalog = {
					...selectedRow,
					name: catalogName,
					fromDate: effectFromDate ? moment.utc(data?.from).format('DD-MMMM-YYYY') : '',
					toDate: effectToDate ? moment.utc(data?.to).format('DD-MMMM-YYYY') : '',
					published: draft,
				};
				setAlert({ message: 'Catalog Updated Successfully', severity: 'success' });
				setSelectedRow(updatedCatalog);
				setEditCatalog(false);
				dispatch(setSupplierCatalog(updatedCatalog));
				setBtnLoading(false);
				setDisable(false);
				setApiCall(true);
			},
			(error: AxiosError) => {
				console.log(error);
				displayCustomError(error);
				setBtnLoading(false);
				setDisable(false);
			},
			catalogId
		);
	};

	const handleSearch = (value: any) => {
		setSearch(value);

		// if (value?.length === 0) {
		// 	setFilteredProducts([]);
		// 	return;
		// }
		const filteredProducts: any = catalogProducts?.filter((product: Product) => {
			const lowerCaseValue = value?.toLowerCase();

			// Check if the catalog's name, category or id includes the search value
			return (
				product?.name?.toLowerCase()?.includes(lowerCaseValue) ||
				product?.id?.toString()?.includes(lowerCaseValue) ||
				product?.category?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.machineModel?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.machineBrand?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.brand?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.subCategory?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.partDimension?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.partDesign?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.partWeight?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.partComposition?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.groupCategory?.toString().toLowerCase().includes(lowerCaseValue) ||
				product?.sku?.toString().toLowerCase().includes(lowerCaseValue)
			);
		});
		setFilteredProducts(filteredProducts);
	};

	const handleEditClick = (params: any) => {
		const updatedCatalogs = catalogProducts?.map((catalog) => {
			if (params?.id === catalog?.id) {
				return { ...catalog, edit: true, focus: true };
			}
			return catalog;
		});
		const updatedFilteredCatalogs = filteredProducts?.map((catalog) => {
			if (params?.id === catalog?.id) {
				return { ...catalog, edit: true, focus: true };
			}
			return catalog;
		});
		setFilteredProducts(updatedFilteredCatalogs);
		setCatalogProducts(updatedCatalogs);
	};

	const handleEditClose = (params: any) => {
		const updatedCatalogs = catalogProducts.map((catalog) => {
			if (params?.id === catalog.id) {
				return { ...catalog, edit: false, customerPrice: catalog?.oldCustomerPrice };
			}
			return catalog;
		});
		const updatedFilteredCatalogs = filteredProducts.map((catalog) => {
			if (params?.id === catalog.id) {
				return {
					...catalog,
					edit: false,
					customerPrice: catalog?.oldCustomerPrice
						? catalog?.oldCustomerPrice
						: catalog.customerPrice,
					oldCustomerPrice: undefined,
				};
			}
			return catalog;
		});
		setFilteredProducts(updatedFilteredCatalogs);
		setCatalogProducts(updatedCatalogs);
	};

	const handleUpdate = (params: any) => {
		const updatedProducts = catalogProducts.map((catalog) => {
			if (params?.id === catalog?.id) {
				return {
					...catalog,
					edit: false,
					focus: false,
					customerPrice: formatPriceValue(catalog?.customerPrice),
					oldCustomerPrice: undefined,
				};
			}
			return catalog;
		});

		const updatedFilteredProducts = filteredProducts.map((catalog) => {
			if (params?.id === catalog?.id) {
				return {
					...catalog,
					edit: false,
					focus: false,
					customerPrice: formatPriceValue(catalog?.customerPrice),
					oldCustomerPrice: undefined,
				};
			}
			return catalog;
		});

		// Update selected items
		const updatedSelectItems = selectedItems.map((selectedItem) => {
			const correspondingProduct = updatedProducts.find(
				(product) => product?.id === selectedItem?.id
			);
			if (correspondingProduct) {
				return { ...selectedItem, customerPrice: correspondingProduct.customerPrice };
			}
			return selectedItem;
		});

		let data;
		if (filteredProducts?.length > 0) {
			data = updatedFilteredProducts?.map((product: Product) => {
				return { id: product?.id, price: product?.customerPrice };
			});
		} else {
			data = updatedProducts?.map((product: Product) => {
				return { id: product?.id, price: product?.customerPrice };
			});
		}

		apiService.editProducts(
			{ products: data },
			(response: AxiosResponse) => {
				setAlert({ message: 'Product Updated Successfully', severity: 'success' });
				setCatalogProducts(updatedProducts);
				setFilteredProducts(updatedFilteredProducts);
				setSelectedItems(updatedSelectItems);
			},
			(error: AxiosError) => {
				displayCustomError(error);
			},
			catalogId
		);
	};

	const handleEditUnselectedProduct = (params: any) => {
		const updatedCatalogs = products?.map((catalog) => {
			if (params?.id === catalog?.id) {
				return { ...catalog, edit: true, focus: true };
			}
			return catalog;
		});
		setProducts(updatedCatalogs);
	};

	const handleEditCloseUnselectedProduct = (params: any) => {
		const updatedCatalogs = products.map((catalog) => {
			if (params?.id === catalog.id) {
				return { ...catalog, edit: false };
			}
			return catalog;
		});
		setProducts(updatedCatalogs);
	};

	const handleUpdateUnselectedProduct = (params: any) => {
		const updatedProducts = products?.map((catalog) => {
			if (params.id === catalog.id) {
				return {
					...catalog,
					edit: false,
					focus: false,
					customerPrice: formatPriceValue(catalog?.customerPrice),
				};
			}
			return catalog;
		});
		setSelectedProducts((prevState: any) => {
			return prevState?.map((product: any) => {
				if (product?.id === params.id) {
					return { ...product, customerPrice: formatPriceValue(params?.row?.customerPrice) };
				}
				return product;
			});
		});
		setProducts(updatedProducts);
	};

	const handleCustomerPriceUnselectedProducts = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		params: any
	) => {
		const inputValue = e.target.value;

		if (priceRegex.test(inputValue)) {
			const updatedProducts: Product[] = products?.map((catalog) => {
				if (params.id === catalog.id) {
					return { ...catalog, customerPrice: inputValue };
				}
				return catalog;
			});
			setProducts(updatedProducts);
		}
	};
	const showDetails = (product: any) => {
		setProductDetails(product);
		setDetailsModal(true);
	};

	const handleCustomerPrice = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		params: any
	) => {
		const inputValue = e.target.value;
		if (priceRegex.test(inputValue)) {
			const updatedProducts: Product[] = catalogProducts?.map((product: Product) => {
				if (params.id === product?.id) {
					return {
						...product,
						customerPrice: inputValue,
						oldCustomerPrice:
							product?.oldCustomerPrice === undefined
								? product.customerPrice
								: product?.oldCustomerPrice,
					};
				}
				return product;
			});

			const updatedFilterProducts: Product[] = filteredProducts?.map((product: Product) => {
				if (params.id === product?.id) {
					return {
						...product,
						customerPrice: inputValue,
						oldCustomerPrice:
							product?.oldCustomerPrice === undefined
								? product.customerPrice
								: product?.oldCustomerPrice,
					};
				}
				return product;
			});
			setFilteredProducts(updatedFilterProducts);
			setCatalogProducts(updatedProducts);
		}
	};

	const columns = [
		// {
		// 	field: 'id',
		// 	headerName: 'Product ID',
		// 	width: 100,
		// 	headerAlign: 'center',
		// 	align: 'center',
		// 	renderCell: (params: any) => {
		// 		const productId = params.id;
		// 		return <div title={productId}>{truncateField(productId, 32)}</div>;
		// 	},
		// },
		{
			field: 'name',
			headerName: 'Part Name',
			width: 300,
			renderCell: (params: any) => {
				const name = params?.row?.name;
				return (
					<div
						style={{
							display: 'flex',
							gap: '8px',
							alignItems: 'center',
							maxWidth: '300px',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
						title={name}
					>
						<img
							style={{ objectFit: 'contain' }}
							src={
								params?.row?.images?.[0]?.image ||
								params?.row?.images?.[0]?.url ||
								params?.row?.image ||
								'/placeholderImage.svg'
							}
							alt="product-img"
							width="24px"
							height="24px"
						/>
						{params?.row?.name?.length > 33 ? (
							<CustomTooltip title={params?.row?.name} placement="top">
								<p
									style={{
										maxWidth: '260px',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{params?.row?.name}
								</p>
							</CustomTooltip>
						) : (
							<p
								style={{
									maxWidth: '260px',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{params?.row?.name}
							</p>
						)}
					</div>
				);
			},
		},
		// { field: 'unit', headerName: 'Unit', width: 80 },
		{ field: 'price', headerName: 'Price (INR)', width: 100, align: 'center' },
		{ field: 'groupCategory', headerName: 'Part Group Category', width: 180 },
		{
			field: 'category',
			headerName: 'Part Category',
			filterable: true,
			width: 160,
		},
		{
			field: 'customerPrice',
			headerName: 'Customer-Price',
			width: 130,
			renderCell: (params: any) =>
				params?.row?.edit ? (
					<StyledTextFieldCustomerPrice
						id="editPrice"
						variant="standard"
						value={params?.row?.customerPrice}
						autoFocus={params?.row?.focus}
						onChange={(e) => {
							handleCustomerPrice(e, params);
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleUpdate(params);
							}
						}}
						InputProps={{
							disableUnderline: true,
							classes: {
								root: 'MuiInputBase-root',
							},
						}}
					/>
				) : (
					params.row.customerPrice
				),
			align: 'center',
			headerAlign: 'center',
		},
		{ field: 'brand', headerName: 'Brand', width: 150 },
		{ field: 'partNumber', headerName: 'Part Number', width: 150 },
		{ field: 'partDimension', headerName: 'Part Dimension ID', width: 180 },
		{ field: 'partDiameter', headerName: 'Part Diameter ID', width: 180 },
		{ field: 'partWeight', headerName: 'Part Weight', width: 150 },
		{ field: 'partQuality', headerName: 'Part Quality ID', width: 180 },
		{ field: 'partComposition', headerName: 'Part Composition ID', width: 180 },
		{ field: 'partDesign', headerName: 'Part Design ID', width: 180 },
		{ field: 'partPerformance', headerName: 'Part Performance ID', width: 180 },
		{
			field: 'actions',
			headerName: 'Actions',
			width: 120,
			renderCell: (params: any) =>
				params?.row?.edit ? (
					<div
						id="updateBtns"
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '5px',
							width: '100%',
							height: '100%',
						}}
					>
						<IconButton id="updateProductBtn" onClick={() => handleUpdate(params)}>
							<TickIcon id="updateProduct" />
						</IconButton>
						<IconButton id="cancelProductBtn" onClick={() => handleEditClose(params)}>
							<CrossIcon id="cancelProduct" />
						</IconButton>
					</div>
				) : (
					<div
						id="editBtns"
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							width: '100%',
							height: '100%',
						}}
					>
						{params?.row?.editBtnDisable ? (
							<CustomTooltip title="Please Select Item First" placement="top">
								<span>
									<IconButton>
										<EditIcon id="editProduct1" />
									</IconButton>
								</span>
							</CustomTooltip>
						) : (
							<IconButton
								disabled={params?.row?.editBtnDisable}
								onClick={() => handleEditClick(params)}
								id="editProductBtn"
							>
								<EditIcon id="editProduct" />
							</IconButton>
						)}
					</div>
				),
			align: 'center',
			headerAlign: 'center',
		},
	];

	const unSelectedProductsColumn = [
		// {
		// 	field: 'id',
		// 	headerName: 'Product ID',
		// 	width: 100,
		// 	headerAlign: 'center',
		// 	align: 'center',
		// 	renderCell: (params: any) => {
		// 		const productId = params.row.id;
		// 		return <div title={productId}>{truncateField(productId, 32)}</div>;
		// 	},
		// },
		{
			field: 'name',
			headerName: 'Part Name',
			width: 300,
			renderCell: (params: any) => {
				const name = params?.row?.name;
				return (
					<div
						style={{
							display: 'flex',
							gap: '8px',
							alignItems: 'center',
							maxWidth: '300px',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
						title={name}
					>
						<img
							style={{ objectFit: 'contain' }}
							src={
								params?.row?.images?.[0]?.image ||
								params?.row?.images?.[0]?.url ||
								params?.row?.image ||
								'/placeholderImage.svg'
							}
							alt="product-img"
							width="24px"
							height="24px"
						/>
						{params?.row?.name?.length > 33 ? (
							<CustomTooltip title={params?.row?.name} placement="top">
								<p
									style={{
										maxWidth: '260px',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{params?.row?.name}
								</p>
							</CustomTooltip>
						) : (
							<p
								style={{
									maxWidth: '260px',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{params?.row?.name}
							</p>
						)}
					</div>
				);
			},
		},
		// { field: 'unit', headerName: 'Unit', width: 80 },
		{ field: 'price', headerName: 'Price (INR)', width: 100, align: 'center' },
		{ field: 'groupCategory', headerName: 'Part Group Category', width: 180 },
		{
			field: 'category',
			headerName: 'Part Category',
			filterable: true,
			width: 160,
		},
		{
			field: 'customerPrice',
			headerName: 'Customer-Price',
			width: 130,
			renderCell: (params: any) =>
				params?.row?.edit ? (
					<StyledTextFieldCustomerPrice
						id="editPrice"
						variant="standard"
						value={params?.row?.customerPrice}
						autoFocus={params?.row?.focus}
						onChange={(e) => {
							handleCustomerPriceUnselectedProducts(e, params);
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleUpdateUnselectedProduct(params);
							}
						}}
						InputProps={{
							disableUnderline: true,
							classes: {
								root: 'MuiInputBase-root',
							},
						}}
					/>
				) : (
					params.row.customerPrice
				),
			align: 'center',
			headerAlign: 'center',
		},
		{ field: 'brand', headerName: 'Brand', width: 150 },
		{ field: 'partNumber', headerName: 'Part Number', width: 150 },
		{ field: 'partDimension', headerName: 'Part Dimension ID', width: 180 },
		{ field: 'partDiameter', headerName: 'Part Diameter ID', width: 180 },
		{ field: 'partWeight', headerName: 'Part Weight', width: 150 },
		{ field: 'partQuality', headerName: 'Part Quality ID', width: 180 },
		{ field: 'partComposition', headerName: 'Part Composition ID', width: 180 },
		{ field: 'partDesign', headerName: 'Part Design ID', width: 180 },
		{ field: 'partPerformance', headerName: 'Part Performance ID', width: 180 },

		{
			field: 'actions',
			headerName: 'Actions',
			width: 120,
			renderCell: (params: any) =>
				params?.row?.edit ? (
					<div
						id="updateBtnUnselectedItem"
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '5px',
							width: '100%',
							height: '100%',
						}}
					>
						<IconButton
							id="updateProductUnselectedItemBtn"
							onClick={() => handleUpdateUnselectedProduct(params)}
						>
							<TickIcon id="updateProductUnselectedItem" />
						</IconButton>
						<IconButton
							id="cancelProductUnselectedItemBtn"
							onClick={() => handleEditCloseUnselectedProduct(params)}
						>
							<CrossIcon id="cancelProductUnselectedItem" />
						</IconButton>
					</div>
				) : (
					<div
						id="editBtnUnselectedItem"
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							width: '100%',
							height: '100%',
						}}
					>
						{params?.row?.editBtnDisable ? (
							<CustomTooltip title="Please Select Item First" placement="top">
								<span>
									<IconButton>
										<EditIcon id="editProductUnselectedItem1" />
									</IconButton>
								</span>
							</CustomTooltip>
						) : (
							<IconButton
								disabled={params?.row?.editBtnDisable}
								onClick={() => handleEditUnselectedProduct(params)}
								id="editProductUnselectedItemBtn"
							>
								<EditIcon id="editProductUnselectedItem" />
							</IconButton>
						)}
					</div>
				),
			align: 'center',
			headerAlign: 'center',
		},
	];
	const disabledCatalog = () => {
		if (!catalogName || !effectFromDate || !effectToDate || disable) {
			return true;
		}
		return false;
	};
	const handleClose = () => {
		setEditCatalog(false);
		setDraft(selectedRow?.published);
		setAddProductModal(false);
		setPage(1);
	};

	const onClose = () => {
		setPage(1);
		setProducts((prevState: Product[]) => {
			return prevState?.map((product: Product) => {
				return { ...product, selected: false };
			});
		});
		setSelectedItemIds((prevState) => []);
		setSelectedItems([]);
		setSelectedProducts([]);
	};

	const handleSpecificProductSelect = (id: any) => {
		const ids: string[] = [];
		setProducts((prevState: any) => {
			return prevState?.map((item: any) => {
				if (item?.id === id) {
					return {
						...item,
						isSelection: !item?.isSelection,
					};
				}
				return item;
			});
		});
		setFilteredProducts((prevState: any) => {
			return prevState?.map((item: any) => {
				if ((item?.id !== id && item?.isSelection) || (item?.id === id && !item?.isSelection)) {
					ids.push(item?.id);
				}
				if (item?.id === id) {
					return {
						...item,
						isSelection: !item?.isSelection,
					};
				}
				return item;
			});
		});
		//@ts-ignore
		setCatalogProductIds(ids);
	};

	const showDetailsUP = (product: any) => {
		setProductDetails(product);
		setDetailsModal(true);
	};

	const onRowsSelectionHandler = (ids: any) => {
		setCatalogProductIds(ids);
	};

	const handleSpecificProductSelectUnselectedProducts = (id: any) => {
		const ids: string[] = [];

		products?.forEach((item: any) => {
			if ((item?.id !== id && item?.isSelection) || (item?.id === id && !item?.isSelection)) {
				ids.push(String(item?.id));
			}
		});

		//@ts-ignore
		onUnselectedProductsSelectionHandler(ids);
	};

	const onUnselectedProductsSelectionHandler = (ids: any) => {
		const unselectedProductIds: string[] = [];
		const selectedProducts: Product[] = products?.filter((product: Product) =>
			ids?.includes(product?.id)
		);

		const updatedProducts: Product[] = products?.map((product: Product) => {
			if (ids?.includes(product?.id)) {
				return { ...product, editBtnDisable: false, selected: true, isSelection: true };
			}
			unselectedProductIds.push(product?.id);
			return { ...product, editBtnDisable: true, selected: false, isSelection: false };
		});
		setProducts(updatedProducts);

		//Maintain other pages selected items
		setSelectedItemIds((prevState: string[]) => {
			const filterIds = prevState?.filter((id) => !unselectedProductIds?.includes(id));
			return [...new Set([...filterIds, ...ids])];
		});
		setSelectedProducts((prevState: any) => {
			const uniqueIds = new Set(prevState.map((product: any) => product?.id));
			const filteredData = selectedProducts.filter((product: any) => !uniqueIds.has(product?.id));
			const filteredPrevState = prevState?.filter(
				(product: any) => !unselectedProductIds?.includes(product?.id)
			);

			return [...filteredPrevState, ...filteredData];
		});
	};
	const handleRowClick = (params: any, event: any) => {
		const id = event?.target?.id;
		if (
			![
				'editProduct',
				'cancelProduct',
				'updateProduct',
				'editProductBtn',
				'cancelProductBtn',
				'updateProductBtn',
				'editPrice',
				'editBtns',
				'updateBtns',
				'editProduct1',
			]?.includes(id) &&
			!params?.row?.edit
		) {
			const data = catalogProducts?.find((catalog: any) => catalog?.id === params?.id);
			const details: any = [
				{
					name: data?.name,
					images: data?.images?.map((image: any) => {
						return { src: image?.image, alt: 'product-img' };
					}),
					image: data?.image,
					description: data?.description,
					details: [
						`Part Category: ${data?.category}`,
						`Unit: ${data?.unit}`,
						`Price: ${data?.price}`,
					],
				},
			];
			setProductDetailsData(details);
			setProductDetailsModal(true);
		}
	};

	const handleUnselectedProductClick = (params: any, event: any) => {
		const id = event?.target?.id;
		if (
			![
				'editProductUnselectedItemBtn',
				'cancelProductUnselectedItemBtn',
				'updateProductUnselectedItemBtn',
				'editProductUnselectedItem',
				'cancelProductUnselectedItem',
				'updateProductUnselectedItem',
				'editPriceUnselectedItem',
				'editBtnUnselectedItem',
				'updateBtnUnselectedItem',
			]?.includes(id) &&
			!params?.row?.edit
		) {
			const data = products?.find((catalog: any) => catalog?.id === params?.id);
			const details: any = [
				{
					name: data?.name,
					images: data?.images?.map((image: any) => {
						return { src: image?.image, alt: 'product-img' };
					}),
					image: data?.image,
					description: data?.description,
					details: [
						`Part Category: ${data?.category}`,
						`Unit: ${data?.unit}`,
						`Price: ${data?.price}`,
					],
				},
			];
			setProductDetailsData(details);
			setProductDetailsModal(true);
		}
	};

	const handleConfirm = () => {
		setBtnLoading(true);
		const ids = catalogProductIds?.map((id: string) => Number(id));
		apiService.deleteProducts(
			ids,
			(response: AxiosResponse) => {
				setAlert({
					message:
						catalogProductIds?.length > 1
							? 'Products Deleted Successfully'
							: 'Product Deleted Successfully',
					severity: 'success',
				});
				setApiCallProducts(true);
				setdeleteProductModal(false);
				setBtnLoading(false);
				setCatalogProductIds([]);
				setSearch('');
			},
			(error: AxiosError) => {
				console.log(error);
				displayCustomError(error);
				setBtnLoading(false);
			},
			catalogId
		);
	};

	const addProduct = () => {
		setBtnLoading(true);
		const productsData = selectedProducts?.map((item: any) => {
			return {
				id: item?.id,
				price: item?.customerPrice || item?.price,
			};
		});

		apiService.addProducts(
			{ products: productsData },
			(response: AxiosResponse) => {
				setApiCallProducts(true);
				setAlert({
					message: 'Product Added Successfully',
					severity: 'success',
				});
				setSearchValue('');
				setBtnLoading(false);
				handleClose();
				onClose();
			},
			(error: AxiosError) => {
				displayCustomError(error);
				setBtnLoading(false);
			},
			catalogId
		);
	};

	const handleEditCatalog = () => {
		setEditCatalog(true);
	};

	const setCatalogFieldValues = (supplierCatalog: Catalog) => {
		const { name, fromDate, toDate, published, restaurantId } = supplierCatalog;
		setCatalogName(name);
		//@ts-ignore
		setEffectFromDate(moment.utc(fromDate, 'DD-MMMM-YYYY'));
		//@ts-ignore
		setEffectToDate(moment.utc(toDate, 'DD-MMMM-YYYY'));
		setDraft(published);
		if (restaurantId) {
			setCustomerId(restaurantId);
		}
	};

	const catalogUnEditView = () => {
		if (windowDimensions?.width > 1100) {
			return (
				<EditContainer>
					<GridContainer>
						<GridRow>
							<GridItem>
								Catalog Name:<span>{selectedRow?.name}</span>
							</GridItem>
							<GridItem>
								Customer Name: <span>{selectedRow?.customerName}</span>
							</GridItem>
							<GridItem width="200px">
								Status:{' '}
								<span style={{ color: selectedRow?.published ? '#24963E' : '#E6AE06' }}>
									{selectedRow?.published ? 'Published' : 'Draft'}
								</span>
							</GridItem>
						</GridRow>
						<GridRow>
							<GridItem>
								From: <span>{selectedRow?.fromDate}</span>
							</GridItem>
							<GridItem>
								To: <span>{selectedRow?.toDate}</span>
							</GridItem>
						</GridRow>
					</GridContainer>
					<IconButton style={{ height: '50px', width: '50px' }} onClick={handleEditCatalog}>
						<EditIcon />
					</IconButton>
				</EditContainer>
			);
		} else if (windowDimensions?.width > 750) {
			return (
				<CatalogForm>
					<div style={fieldContainerStyling}>
						<CatalogFields width="250px">
							Catalog Name: <span style={{ margin: '0 5px' }}>{selectedRow?.name}</span>
						</CatalogFields>
						<CatalogFields width="250px">
							Customer Name: <span style={{ margin: '0 5px' }}>{selectedRow?.customerName}</span>
						</CatalogFields>
					</div>
					<div style={fieldContainerStyling}>
						<CatalogFields width="250px">
							From: <span style={{ margin: '0 5px' }}>{selectedRow?.fromDate}</span>
						</CatalogFields>
						<CatalogFields width="250px">
							To: <span style={{ margin: '0 5px' }}>{selectedRow?.toDate}</span>
						</CatalogFields>
					</div>
					<div style={fieldContainerStyling}>
						<CatalogFields width="250px">
							Status:{' '}
							<span
								style={{
									color: selectedRow?.published ? '#24963E' : '#E6AE06',
									margin: '0 5px',
								}}
							>
								{selectedRow?.published ? 'Published' : 'Draft'}
							</span>
						</CatalogFields>
						<IconButton style={{ width: '250px' }} onClick={handleEditCatalog}>
							<EditIcon />
						</IconButton>
					</div>
				</CatalogForm>
			);
		} else {
			return (
				<CatalogForm>
					<CatalogFields>
						Catalog Name: <span style={{ margin: '0 5px' }}>{selectedRow?.name}</span>
					</CatalogFields>
					<CatalogFields>
						Customer Name: <span style={{ margin: '0 5px' }}>{selectedRow?.customerName}</span>
					</CatalogFields>
					<CatalogFields>
						From: <span style={{ margin: '0 5px' }}>{selectedRow?.fromDate}</span>
					</CatalogFields>
					<CatalogFields>
						To: <span style={{ margin: '0 5px' }}>{selectedRow?.toDate}</span>
					</CatalogFields>
					<div
						style={{
							display: 'flex',
							width: '320px',
							height: '22px',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<CatalogFields width="200px">
							Status:{' '}
							<span
								style={{
									color: selectedRow?.published ? '#24963E' : '#E6AE06',
									margin: '0 5px',
								}}
							>
								{selectedRow?.published ? 'Published' : 'Draft'}
							</span>
						</CatalogFields>
						<IconButton style={{ width: '50px' }} onClick={handleEditCatalog}>
							<EditIcon />
						</IconButton>
					</div>
				</CatalogForm>
			);
		}
	};

	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
		}
	};

	const filterUnselectedItems = (selectedProducts: Product[], products: Product[]) => {
		const selectedProductIds = selectedProducts?.map((product: any) => product?.id);
		const unselectedProducts = products?.map((product) => {
			if (selectedProductIds?.includes(product?.id)) {
				return { ...product, disableRow: true };
			}
			return product;
		});

		if (page === 1) {
			setProducts(unselectedProducts);
		} else {
			setProducts((prevState: any) => [...prevState, ...unselectedProducts]);
		}
	};

	const getCustomerPrice = (product: Product) => {
		if (!product?.customerPrice) {
			const selectedProduct = selectedProducts?.find(
				(item: any) => item?.id === product?.id?.toString()
			);
			return selectedProduct?.customerPrice || product?.price;
		}
		return product?.customerPrice || product?.price;
	};

	const getProducts = (catalogProducts: Product[]) => {
		apiService.getProducts(
			(response: AxiosResponse) => {
				const productsData = response?.data?.data;
				const data = productsData?.products?.map((product: any) => {
					const selectedState = selectedItemIds.includes(product?.id?.toString());
					return {
						...product,
						id: product?.id?.toString(),
						price: product?.price,
						categoryId: product?.category?.id,
						category: product?.category?.name,
						categoryObject: { id: product?.category?.id, value: product?.category?.name },
						customerPrice: getCustomerPrice(product),
						image: product?.image,
						editBtnDisable: !selectedState,
						selected: selectedState,
						machineBrand: product?.machineBrand?.name,
						machineBrandId: product?.machineBrand?.id,
						machineModel: product?.machineModel?.name,
						machineModelId: product?.machineModel?.id,
						subCategoryId: product?.subCategory?.id,
						subCategory: product?.subCategory?.name,
						groupCategory: product?.groupCategory?.name,
						groupCategoryId: product?.groupCategory?.id,
						brand: product?.brand?.name,
						brandId: product?.brand?.id,
						partComposition: product?.partComposition?.name,
						partCompositionId: product?.partComposition?.id,
						partDesign: product?.partDesign?.name,
						partDesignId: product?.partDesign?.id,
						partDiameter: product?.partDiameter?.name,
						partDiameterId: product?.partDiameter?.id,
						partDimension: product?.partDimension?.name,
						partDimensionId: product?.partDimension?.id,
						partOwnership: product?.partOwnership?.name,
						partOwnershipId: product?.partOwnership?.id,
						partQuality: product?.partQuality?.name,
						partQualityId: product?.partQuality?.id,
						partWeight: product?.partWeight?.name,
						partWeightId: product?.partWeight?.id,
						partPerformance: product?.partPerformance?.name,
						partPerformanceId: product?.partPerformance?.id,
					};
				});
				const totalCount = productsData?.totalCount;
				const totalPages = Math.max(Math.ceil(totalCount / 25), 1);

				setProductCount(totalCount);
				setTotalPages(totalPages);
				// dispatch(setAllProducts(data));
				filterUnselectedItems(catalogProducts, data);
				setLoading(false);
				setSearching(false);
				setLoadingProducts(false);
				if (apiCallProducts) {
					setApiCallProducts(false);
				}
			},
			(error: AxiosError) => {
				displayCustomError(error);
				setSearching(false);
				setLoading(false);
			},
			{
				page: page,
				limit: 25,
				category: categoryId,
				search: searchValue,
				machineModel: filtersMC?.model?.id,
				subCategory: filtersMC?.subCategory?.id,
				machineBrand: filtersMC?.machineBrand?.id,
				dimension: filtersMC?.dimension?.id,
				design: filtersMC?.design?.id,
				weight: filtersMC?.weight?.id,
				composition: filtersMC?.composition?.id,
				brand: filtersMC?.brand?.id,
			}
		);
	};

	const getCatalogProducts = () => {
		apiService.getCatalogProducts(
			(response: AxiosResponse) => {
				const productsData = response?.data?.data;
				const data = productsData?.map((product: any) => {
					return {
						...product,
						id: product?.productId?.toString() || product?.id?.toString(),
						name: product?.productName || product?.product_name,
						unit: product?.unit,
						price: product?.price,
						category: product?.category?.name,
						categoryId: product?.category?.id,
						categoryObject: { id: product?.category?.id, value: product?.category?.name },
						customerPrice: product?.customerPrice,
						description: product?.description,
						image: product?.image,
						machineBrand: product?.machineBrand?.name,
						machineBrandId: product?.machineBrand?.id,
						machineModel: product?.machineModel?.name,
						machineModelId: product?.machineModel?.id,
						subCategoryId: product?.subCategory?.id,
						subCategory: product?.subCategory?.name,
						groupCategory: product?.groupCategory?.name,
						groupCategoryId: product?.groupCategory?.id,
						brand: product?.brand?.name,
						brandId: product?.brand?.id,
						partComposition: product?.partComposition?.name,
						partCompositionId: product?.partComposition?.id,
						partDesign: product?.partDesign?.name,
						partDesignId: product?.partDesign?.id,
						partDiameter: product?.partDiameter?.name,
						partDiameterId: product?.partDiameter?.id,
						partDimension: product?.partDimension?.name,
						partDimensionId: product?.partDimension?.id,
						partOwnership: product?.partOwnership?.name,
						partOwnershipId: product?.partOwnership?.id,
						partQuality: product?.partQuality?.name,
						partQualityId: product?.partQuality?.id,
						partWeight: product?.partWeight?.name,
						partWeightId: product?.partWeight?.id,
						partPerformance: product?.partPerformance?.name,
						partPerformanceId: product?.partPerformance?.id,
					};
				});
				setSelectedRow(supplierCatalog);
				setCatalogFieldValues(supplierCatalog);
				setFilteredProducts(data);
				setCatalogProducts(data);

				//next call
				getProducts(data);
			},
			(error: AxiosError) => {
				displayCustomError(error);
				setLoading(false);
			},
			catalogId
		);
	};

	useEffect(() => {
		if (canViewProduct) {
			getCatalogProducts();
		}
	}, [apiCallProducts, page, categoryId, filtersMC]);

	//debounce
	useEffect(() => {
		if (canViewProduct) {
			//debounce
			if (timeoutHandler.current) {
				clearTimeout(timeoutHandler.current);
			}
			timeoutHandler.current = setTimeout(() => {
				try {
					getCatalogProducts();
				} catch (err) {}
			}, 500);
		} else {
		}
	}, [searchValue, canViewProduct]);

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	if (loading) {
		return (
			<Box
				sx={{
					width: '100%',
					height: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<div className="chat__body__loadingMore__wrapper">
					<CircularProgress size={28} />
				</div>
			</Box>
		);
	}

	return (
		<>
			{canUpdateCatalogs ? (
				<>
					<Container>
						{detailsMiodal ? (
							<CustomModal
								open={detailsMiodal}
								setOpen={setDetailsModal}
								style={productDetailsModalStyles}
								title={'Product Details'}
								children={<ProductDetails setOpenModal={setDetailsModal} data={productDetails} />}
								header={false}
							/>
						) : null}
						{addProductModal ? (
							// <CustomModal
							// 	open={addProductModal}
							// 	setOpen={setAddProductModal}
							// 	title="Add Product"
							// 	style={
							// 		windowDimensions?.width <= 750 ? { maxWidth: 'auto', maxHeight: 'auto' } : {}
							// 	}
							// 	children={
							// 		<ProductAdd
							// 			productOptions={notSelectedOptions}
							// 			setProductOptions={setNotSelectedOptions}
							// 			products={notSelectedProducts}
							// 			setProducts={setNotSelectedProducts}
							// 			catalogProducts={catalogProducts}
							// 			setCatalogProducts={setCatalogProducts}
							// 			setFilteredProducts={setFilteredProducts}
							// 			setSearchValue={setSearchValue}
							// 			handleClose={() => setAddProductModal(false)}
							// 			setAlert={setAlert}
							// 			displayCustomError={displayCustomError}
							// 			setApiCallProducts={setApiCallProducts}
							// 			windowDimensions={windowDimensions}
							// 		/>
							// 	}
							// />
							<CustomDrawer
								open={addProductModal}
								setOpen={setAddProductModal}
								onClose={onClose}
								title="Add Product"
								header
								style={
									windowDimensions?.width >= 750
										? { width: '1118px', height: '100vh' }
										: { width: '100%', height: '100vh' }
								}
								children={
									<Box sx={{ padding: '16px' }}>
										<PaginatedProductData
											categoryOptions={getUniqueObjectsByKey(products, 'categoryId')}
											rows={products}
											columns={unSelectedProductsColumn}
											onRowsSelectionHandler={onUnselectedProductsSelectionHandler}
											handleRowClick={handleUnselectedProductClick}
											page={page}
											setPage={setPage}
											totalPages={totalPages}
											setCategoryId={setCategoryId}
											handleChange={handlePageChange}
											selectedItemIds={selectedItemIds}
											searchValue={searchValue}
											setSearchValue={setSearchValue}
											searching={searching}
											setSearching={setSearching}
											loading={btnLoading}
											addProduct={addProduct}
											controlRowSelection
											productCount={productCount}
											style={{
												transition: 'height 0.3s ease',
												maxHeight: 'calc(100vh - 210px)',
												minHeight: 'calc(100vh - 210px)',
											}}
											filters={filtersMC}
											setFilters={setFiltersMC}
											productContainerHeight={'calc(100vh - 400px)'}
											handleSpecificProductSelect={handleSpecificProductSelectUnselectedProducts}
											showDetails={showDetailsUP}
											loadingProducts={loadingProducts}
											setLoadingProducts={setLoadingProducts}
										/>
									</Box>
								}
							/>
						) : null}
						{deleteProductModal ? (
							<CustomModal
								open={deleteProductModal}
								setOpen={setdeleteProductModal}
								title="Remove Items"
								style={stylingDeleteModal}
								children={
									<ConfirmModal
										item={{ count: catalogProductIds?.length, name: selectedRow?.customerName }}
										loading={btnLoading}
										handleClose={() => setdeleteProductModal(false)}
										handleConfirm={handleConfirm}
									/>
								}
							/>
						) : null}
						{editCatalog ? (
							<CatalogContainer
								draft={draft}
								setDraft={setDraft}
								catalogName={catalogName}
								setCatalogName={setCatalogName}
								effectToDate={effectToDate}
								setEffectToDate={setEffectToDate}
								effectFromDate={effectFromDate}
								setEffectFromDate={setEffectFromDate}
								loading={btnLoading}
								customers={customers}
								customerId={customerId}
								editCatalog={editSupplierCatalog}
								handleClose={handleClose}
								disabledCatalog={disabledCatalog}
							/>
						) : (
							catalogUnEditView()
						)}

						<StyledDataGridHeader>
							<SearchBar
								style={{
									width: '328px',
									height: '36px',
								}}
								value={search}
								onChange={handleSearch}
								isLoading={false}
							/>
							<StyledBtnConatiner>
								{catalogProductIds?.length ? (
									<SelectedItemsContainer>
										<Typography>{catalogProductIds?.length} items selected</Typography>
										<IconButton onClick={() => setdeleteProductModal(true)}>
											<DeleteIcon />
										</IconButton>
									</SelectedItemsContainer>
								) : null}
								{windowDimensions?.width > 600 ? (
									<CustomButton
										title="Add Product"
										handleClick={() => {
											setAddProductModal(true);
										}}
										heightVariant="medium"
										styles={{
											width: '116px',
											height: '36px',
											borderRadius: '4px',
											padding: '7px 12px',
										}}
									/>
								) : null}
							</StyledBtnConatiner>
						</StyledDataGridHeader>
						<CustomFilter
							filters={filters}
							setFilters={setFilters}
							activeFilter={activeFilter}
							setActiveFilter={setActiveFilter}
							products={catalogProducts}
							setFilteredProducts={setFilteredProducts}
						/>
						<Divider />
						{catalogProducts?.length !== 0 ? (
							// <CustomDataGrid2
							// 	filterOptions={getUniqueObjectsByKey(filteredProducts, 'categoryId')}
							// 	style={{
							// 		transition: 'height 0.3s ease',
							// 		maxHeight: 'calc(100vh - 370px)',
							// 		minHeight: 'calc(100vh - 370px)',
							// 	}}
							// 	rows={filteredProducts}
							// 	columns={columns}
							// 	onRowsSelectionHandler={onRowsSelectionHandler}
							// 	handleRowClick={handleRowClick}
							// />

							<div style={{}}>
								{filteredProducts?.map((product: any) => {
									return (
										<ProductCard
											data={product}
											checkBoxSelection={handleSpecificProductSelect}
											showDetails={showDetails}
											handleEditClick={handleEditClick}
											handleUpdate={handleUpdate}
											handleEditClose={handleEditClose}
											StyledTextFieldCustomerPrice={StyledTextFieldCustomerPrice}
											handleCustomerPrice={handleCustomerPrice}
										/>
									);
								})}
							</div>
						) : (
							<DataNotFound
								content="You haven't created any products yet."
								operation="create"
								onClick={() => setAddProductModal(true)}
								height="40vh"
							/>
						)}
						{catalogProducts?.length !== 0 ? (
							<SelectedItemsContainer style={{ justifyContent: 'flex-end', marginRight: '16px' }}>
								<Typography>Total Items: {catalogProducts?.length}</Typography>
							</SelectedItemsContainer>
						) : null}
					</Container>
					{windowDimensions?.width <= 600 ? (
						!editCatalog ? (
							<StyledBottomBtnContainer>
								<CustomButton
									title="Add Product"
									handleClick={() => setAddProductModal(true)}
									styles={{
										width: '328px',
										height: '36px',
									}}
								/>
							</StyledBottomBtnContainer>
						) : (
							<StyledBottomBtnContainer>
								<CustomButton
									title="Close"
									handleClick={handleClose}
									styles={{
										width: '143px',
										height: '36px',
										background: 'var(--btn-color-grey)',
										color: 'var(--default-text-color)',
										':hover': {
											background: 'var(--btn-hover-color) !important',
										},
									}}
								/>
								<CustomButton
									title={
										loading ? (
											<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
										) : (
											'Update Catalog'
										)
									}
									handleClick={editSupplierCatalog}
									styles={{
										width: '175px',
										height: '36px',
									}}
								/>
							</StyledBottomBtnContainer>
						)
					) : null}
				</>
			) : (
				<CustomPermission
					icon={<LockIcon />}
					title="We are sorry..."
					paragraph="You do not have permission to update catalog. Contact 
		an administrator to get permission."
				/>
			)}
		</>
	);
};
